<script setup>
import { computed } from 'vue'
import { getFormColor } from '../../../composables/useReservationsCalendar.js'
import { getInitials } from '../../../../../helpers.js'
import { trans } from 'laravel-vue-i18n'

// ─── Props & emits ────────────────────────────────────────────────
const props = defineProps({
    reservations:   { type: Array, default: () => [] },
    visibleFormIds: { type: Set, default: () => new Set() },
    selectedDate:   Object, // moment
})

const emit = defineEmits(['open-reservation'])

// ─── Czech locale data ────────────────────────────────────────────
const DOW_LONG = [
    trans('common.dow_long.sun'),
    trans('common.dow_long.mon'),
    trans('common.dow_long.tue'),
    trans('common.dow_long.wed'),
    trans('common.dow_long.thu'),
    trans('common.dow_long.fri'),
    trans('common.dow_long.sat'),
]
const MONTH_NAMES = [
    trans('common.months.jan'),
    trans('common.months.feb'),
    trans('common.months.mar'),
    trans('common.months.apr'),
    trans('common.months.may'),
    trans('common.months.jun'),
    trans('common.months.jul'),
    trans('common.months.aug'),
    trans('common.months.sep'),
    trans('common.months.oct'),
    trans('common.months.nov'),
    trans('common.months.dec'),
]

const today = new Date()
const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

// ─── Helpers ──────────────────────────────────────────────────────
function fmtTime(t) {
    return t ? t.substring(0, 5).replace(/^0/, '') : ''
}

function totalPrice(reservation) {
    if (!reservation.bill_items?.length) return null
    return reservation.bill_items.reduce((sum, item) => sum + (item.unit_price_with_vat * item.amount), 0)
}

function formatPrice(val) {
    if (val === null || val === undefined) return ''
    return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(val)
}

function statusLabel(state) {
    if (state === 'accepted') return trans('reservations.state_accepted_label')
    if (state === 'pending') return trans('reservations.state_pending_label')
    if (state === 'rejected') return trans('reservations.state_rejected_label')
    return state
}

function statusClass(state) {
    if (state === 'accepted') return 'bg-green-100 text-green-700'
    if (state === 'pending') return 'bg-amber-100 text-amber-700'
    if (state === 'rejected') return 'bg-red-100 text-red-600'
    return 'bg-neutral-100 text-neutral-600'
}

function timeDisplay(r) {
    if (r.time_start && r.time_end) {
        return `${fmtTime(r.time_start)} – ${fmtTime(r.time_end)}`
    }
    if (r.date_start && r.date_end && r.date_start !== r.date_end) {
        return `${r.formatted_date ?? r.date_start} – ${r.formatted_date_end ?? r.date_end}`
    }
    if (r.formatted_date) return r.formatted_date
    return r.date ?? ''
}

// ─── Grouping ─────────────────────────────────────────────────────
const groups = computed(() => {
    const yearMonth = props.selectedDate?.format('YYYY-MM') ?? ''

    const filtered = props.reservations.filter(r => {
        if (!props.visibleFormIds.has(r.id_reservation_forms)) return false
        if (r.date) return r.date.startsWith(yearMonth)
        if (r.date_start && r.date_end) {
            // overlaps this month
            const mStart = yearMonth + '-01'
            const mEnd = yearMonth + '-31'
            return r.date_start <= mEnd && r.date_end >= mStart
        }
        return false
    })

    const byDate = {}
    filtered.forEach(r => {
        const key = r.date ?? r.date_start ?? ''
        if (!byDate[key]) byDate[key] = []
        byDate[key].push(r)
    })

    return Object.keys(byDate)
        .sort()
        .map(dateStr => {
            const d = new Date(dateStr)
            const reservations = byDate[dateStr].slice().sort((a, b) => {
                const ta = a.time_start ?? '00:00:00'
                const tb = b.time_start ?? '00:00:00'
                return ta.localeCompare(tb)
            })
            return {
                dateStr,
                day: d.getDate(),
                dowLong: DOW_LONG[d.getDay()],
                monthLabel: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
                isToday: dateStr === todayStr,
                reservations,
            }
        })
})
</script>

<template>
    <div class="flex-1 min-h-0 overflow-y-auto bg-white dark:bg-neutral-900 px-8 py-6">

        <!-- Empty state -->
        <div v-if="!groups.length" class="flex flex-col items-center justify-center h-64 text-neutral-300 dark:text-neutral-600">
            <i class="pi pi-calendar !text-4xl mb-3"/>
            <p class="text-sm">{{ $t('reservations.calendar_empty') }}</p>
        </div>

        <!-- Day groups -->
        <div
            v-for="group in groups"
            :key="group.dateStr"
            class="grid gap-6 pb-5 mb-5 border-b border-neutral-100 dark:border-neutral-800 last:border-0 last:mb-0 last:pb-0"
            style="grid-template-columns: 88px 1fr"
        >
            <!-- Left: date info -->
            <div class="self-start sticky top-0 pt-1">
                <div class="text-[42px] font-semibold leading-none text-neutral-800 dark:text-neutral-100 tracking-tight">
                    {{ group.day }}
                </div>
                <div class="text-[13px] font-semibold text-neutral-600 dark:text-neutral-400 mt-1">
                    {{ group.dowLong }}
                </div>
                <div class="text-[11px] text-neutral-400 dark:text-neutral-500 mt-0.5 uppercase tracking-wide">
                    {{ group.monthLabel }}
                </div>
                <div
                    v-if="group.isToday"
                    class="inline-flex items-center mt-2 px-2 py-0.5 rounded-full bg-blue-100 text-blue-600 text-[10px] font-semibold uppercase tracking-wider"
                >
                    {{ $t('common.today') }}
                </div>
                <div class="text-[11px] text-neutral-400 dark:text-neutral-500 mt-2">
                    {{ group.reservations.length }} {{ $t('reservations.cal_res_suffix') }}
                </div>
            </div>

            <!-- Right: reservation rows -->
            <div class="flex flex-col gap-0.5">
                <div
                    v-for="r in group.reservations"
                    :key="r.id"
                    class="grid items-center gap-3 px-3 py-3 rounded-xl cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                    style="grid-template-columns: 120px 10px 1fr auto auto auto"
                    @click="emit('open-reservation', r)"
                >
                    <!-- Time / date range -->
                    <div class="font-mono text-[13px] font-semibold text-neutral-700 dark:text-neutral-300 truncate">
                        {{ timeDisplay(r) }}
                    </div>

                    <!-- Color dot -->
                    <div
                        class="w-2 h-2 rounded-full flex-shrink-0"
                        :style="{ background: getFormColor(r.reservation_forms) }"
                    />

                    <!-- Main info -->
                    <div class="min-w-0">
                        <div
                            class="text-[14px] font-medium text-neutral-900 dark:text-neutral-100 truncate"
                            :class="r.state === 'rejected' ? 'line-through text-neutral-400' : ''"
                        >
                            {{ r.name }}
                        </div>
                        <div class="text-[12px] text-neutral-400 dark:text-neutral-500 truncate">
                            {{ r.reservation_forms?.name }}
                        </div>
                    </div>

                    <!-- Worker avatar -->
                    <div
                        v-if="r.workers"
                        class="w-7 h-7 rounded-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-[10px] font-bold text-neutral-600 dark:text-neutral-300 flex-shrink-0"
                        :title="r.workers.name"
                    >
                        {{ getInitials(r.workers.name) }}
                    </div>
                    <div v-else class="w-7"/>

                    <!-- Status badge -->
                    <div
                        class="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex-shrink-0"
                        :class="statusClass(r.state)"
                    >
                        {{ statusLabel(r.state) }}
                    </div>

                    <!-- Price -->
                    <div class="text-[12px] font-medium text-neutral-500 dark:text-neutral-400 tabular-nums min-w-[80px] text-right flex-shrink-0">
                        {{ totalPrice(r) !== null ? formatPrice(totalPrice(r)) : '' }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
